import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { FormAlert } from '../components/auth/FormAlert'
import { FormField } from '../components/auth/FormField'
import { apiClient } from '../lib/api/client'
import { useAuth } from '../lib/auth/auth-context'
import { createAccountProblemCopy } from '../lib/auth/problem-messages'
import { safeReturnTo } from '../lib/auth/return-to'

const MAX_ACCOUNT_NAME_LENGTH = 200

/**
 * Reads the `accountName` field off a `domain_join_available` 409 body. This
 * field is real, tested server behavior (join-request.test.ts NT-ONB-D6) but
 * is NOT in the generated type for this operation's error responses — see
 * this file's doc comment on the contract gap. Falls back to an empty string
 * (never `undefined`) so `<RequestToJoinPage>` can render a request rather
 * than crash if the field is ever missing; that page treats a falsy
 * `accountName` as "arrived here without enough context" and shows its own
 * dead-end, so this never surfaces a blank heading.
 */
function readAccountNameFromCollision(problem: unknown): string {
  if (
    problem &&
    typeof problem === 'object' &&
    'accountName' in problem &&
    typeof (problem as { accountName: unknown }).accountName === 'string'
  ) {
    return (problem as { accountName: string }).accountName
  }
  return ''
}

type Step = 'name' | 'confirm'

interface CreateCompanyLocationState {
  readonly email?: string
  readonly returnTo?: string
}

/**
 * "Create your company account" wizard (F0.5, USP-35, PR-ONB-03/06) — the
 * visible half of the provisional-account API (S0.8, `POST
 * /onboarding/account`). Reached from `<LoginPage>`'s 409
 * `onboarding_required` branch: a verified user with no membership yet lands
 * here holding a live pre-onboarding session (`useAuth().status ===
 * 'onboarding_required'`, see `session-store.ts`) whose CSRF token was
 * minted by that login attempt and is already wired into `apiClient` via
 * `authMiddleware` (no manual header-passing needed here).
 *
 * Gate 2 UX spec §2.2 "Create-company wizard": 2 steps (company name, then
 * role confirmation). The spec's optional "invite teammates now (skippable)"
 * step is deliberately NOT built here: it composes with the S0.9 invite
 * endpoint (`POST /onboarding/invitations`, PR-ONB-01) — a distinct
 * PR/story from this one's PR-ONB-03/06 scope. Building it here would be
 * guessing at a flow this story doesn't cover; flagged as a follow-up
 * (F0.9-shaped) rather than invented. Likewise, the spec's "domain
 * auto-extracted [and shown for confirmation]" language is satisfied by
 * DISPLAYING the domain parsed from the already-known verified email — no
 * backend call. Domain MATCH-detection against an existing account (the
 * "join vs. create" branch, PR-ONB-02) is a separate S0.10 flow the
 * contract's openapi.yaml docstring says is deliberately NOT part of
 * `CreateProvisionalAccountRequest` — but the ACTUAL collision check lives
 * in this endpoint's implementation (`provisional-account.ts`'s
 * `domain_collision` guard), which blocks creation and returns `409` with
 * Problem `type: 'domain_join_available'` plus the matched account's
 * display name (HARD-3 disclosure) instead of creating a parallel account.
 * `handleCreate` below detects that response and hands off to
 * `<RequestToJoinPage>` (F0.7, `/onboarding/join`) rather than rendering it
 * as a generic error — see that component's doc comment for the full "how
 * the user gets here" rationale and the contract gap this exposes
 * (`openapi.yaml`'s documented `409` responses for THIS endpoint don't
 * include this shape; it is real, tested server behavior — see
 * `join-request.test.ts` NT-ONB-D6 — just undocumented in the contract).
 *
 * Guarded on `useAuth().status === 'onboarding_required'`: reaching this
 * route any other way (direct link, a stale/expired pre-onboarding session,
 * a page reload that lost the in-memory csrf token) shows a "sign in first"
 * dead-end with a next action, per the UX spec's "every dead-end resolves to
 * a next action" mandate — never a broken form that will 401 on submit.
 */
export function CreateCompanyPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = (location.state as CreateCompanyLocationState | null) ?? null
  const email = locationState?.email
  const returnTo = locationState?.returnTo

  const [step, setStep] = useState<Step>('name')
  const [accountName, setAccountName] = useState('')
  const [accountNameError, setAccountNameError] = useState<string | undefined>()
  const [serverError, setServerError] = useState<
    { title: string; detail: string; showSignInLink?: boolean } | undefined
  >()
  const [submitting, setSubmitting] = useState(false)

  const confirmHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const alertRef = useRef<HTMLDivElement | null>(null)
  const accountNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (step === 'confirm') {
      confirmHeadingRef.current?.focus()
    }
  }, [step])

  useEffect(() => {
    if (serverError) {
      alertRef.current?.focus()
    }
  }, [serverError])

  // USP-42 pattern: a client-validation error must move focus and be
  // announced, matching every other F0.3/F0.4 auth form.
  useEffect(() => {
    if (accountNameError) {
      accountNameInputRef.current?.focus()
    }
  }, [accountNameError])

  function handleContinue() {
    const trimmed = accountName.trim()
    if (!trimmed) {
      setAccountNameError('Enter your company name.')
      return
    }
    if (trimmed.length > MAX_ACCOUNT_NAME_LENGTH) {
      setAccountNameError(`Company name must be ${MAX_ACCOUNT_NAME_LENGTH} characters or fewer.`)
      return
    }
    setAccountNameError(undefined)
    setStep('confirm')
  }

  function handleBack() {
    setStep('name')
    setServerError(undefined)
  }

  async function handleCreate() {
    setServerError(undefined)
    setSubmitting(true)
    try {
      const { data, error } = await apiClient.POST('/onboarding/account', {
        body: { accountName: accountName.trim() },
      })
      if (data) {
        // The 201 body (`ProvisionalAccountResult`) carries `accountContext`
        // but not the full `SessionInfo` shape (no userId/email/csrfToken) —
        // re-probe the session, which now succeeds since `accountContext`
        // exists, to get the real authenticated session into the store.
        await auth.refresh()
        navigate(safeReturnTo(returnTo, '/'), { replace: true })
        return
      }
      // USP-43: a 401 here means the pre-onboarding session's absolute TTL
      // expired mid-wizard (`requireOnboardingUser`,
      // apps/api/src/modules/onboarding/routes.ts) — a distinct, actionable
      // case from every other `already_onboarded`-style Problem this endpoint
      // can return, so it gets its own copy (with a route back to sign-in)
      // rather than the generic `createAccountProblemCopy` fallback, which
      // would misleadingly suggest retrying works. `/onboarding/account` is
      // in `authMiddleware`'s `AUTH_SURFACE_PATHS` (see that file) precisely
      // so this branch — not a global `invalidateSession()` side effect —
      // decides what the user sees; the page keeps rendering the wizard
      // (`auth.status` stays `onboarding_required`) so this alert is never
      // raced out by the early-return below, and the typed company name is
      // never lost.
      if (error?.status === 401) {
        setServerError({
          title: 'Your session expired',
          detail: 'Please sign in again to finish creating your company account.',
          showSignInLink: true,
        })
        return
      }
      // S0.10 collision (see this component's doc comment): the generated
      // client types this endpoint's 409 as a plain `Problem` (no
      // `accountName` field) because openapi.yaml doesn't document this
      // response shape for `/onboarding/account` — but the server DOES send
      // it (join-request.test.ts NT-ONB-D6), so `accountName` is read
      // defensively off the raw error body rather than trusted by type.
      // Hand off to <RequestToJoinPage> instead of rendering a dead-end
      // error: this is the one collision case with a real next action.
      if (error?.type === 'domain_join_available') {
        const collisionAccountName = readAccountNameFromCollision(error)
        navigate('/onboarding/join', {
          replace: true,
          state: { accountName: collisionAccountName, email, returnTo },
        })
        return
      }
      // W1 (USP-35 review): `already_onboarded` leaves the user unable to
      // retry (a first account can only be created once — retrying this
      // form will 409 forever), so — like the 401 session-expired case
      // above — it needs its own next-action affordance rather than just
      // instructional copy with no way forward. Other/unmapped Problem
      // codes fall back to `createAccountProblemCopy`'s generic copy with no
      // link, since we don't know whether signing in again would help.
      const problemCopy = createAccountProblemCopy(error?.type, error?.detail)
      setServerError({
        ...problemCopy,
        showSignInLink: error?.type === 'already_onboarded',
      })
    } catch {
      setServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  // The initial session probe is still in flight. `loading` is not "no
  // pre-onboarding session": a user handed here by the 409 branch would
  // otherwise be told to sign in for a moment before the form appears.
  if (auth.status === 'loading') {
    return null
  }

  if (auth.status !== 'onboarding_required') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Sign in to create a company account</h1>
        <p>We need to confirm it&rsquo;s you before creating your company account.</p>
        <p>
          <Link to="/login">Sign in</Link>
        </p>
      </section>
    )
  }

  const domain = email?.includes('@') ? email.slice(email.indexOf('@') + 1) : undefined

  return (
    <section className="auth-page">
      {step === 'name' && (
        <>
          <h1 className="display-heading">Create your company account</h1>
          {email && (
            <p className="form-field-hint">
              Signing up as <strong>{email}</strong>
              {domain && <> &middot; {domain}</>}
            </p>
          )}
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              handleContinue()
            }}
          >
            <FormField
              ref={accountNameInputRef}
              id="company-name"
              label="Company name"
              type="text"
              autoComplete="organization"
              value={accountName}
              onChange={setAccountName}
              error={accountNameError}
              maxLength={MAX_ACCOUNT_NAME_LENGTH}
              required
            />
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                Continue
              </button>
            </div>
          </form>
        </>
      )}

      {step === 'confirm' && (
        <>
          <h1 className="display-heading" ref={confirmHeadingRef} tabIndex={-1}>
            You&rsquo;ll be the admin for {accountName.trim()}
          </h1>
          <p>
            You can start transacting right away and invite teammates once you&rsquo;re in.{' '}
            <button type="button" className="btn-link" onClick={handleBack}>
              Change company name
            </button>
          </p>
          {serverError && (
            <>
              <FormAlert ref={alertRef} title={serverError.title} detail={serverError.detail} />
              {serverError.showSignInLink && (
                <p>
                  <Link to="/login">Sign in again</Link>
                </p>
              )}
            </>
          )}
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              void handleCreate()
            }}
          >
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  )
}
